/**
 * Dashboard session state is metadata only.
 * Authority is the opaque same-origin BFF cookie via `/api/auth/session` and
 * `/api/resource/me` — never web Storage APIs and never browser bearer tokens.
 */
import { createContext, useCallback, useEffect, useState, ReactNode } from "react";
import type { AuthContextType } from "@/types/auth";
import {
  fetchBffCurrentUser,
  refreshSession,
  validateSession,
  setupAxiosSessionRefresh,
} from "@/helpers";
import {
  SESSION_CHECK_INTERVAL,
  TOKEN_REFRESH_INTERVAL,
} from "@/constants/auth.const";

const ALLOWED_ROLES = ["super_admin", "admin"] as const;

function isAllowedRole(role: string): role is (typeof ALLOWED_ROLES)[number] {
  return ALLOWED_ROLES.includes(role as (typeof ALLOWED_ROLES)[number]);
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthContextType["user"]>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const handleAuthError = useCallback(() => {
    setUser(null);
    setIsLoading(false);
    setIsFetching(false);
    setIsAuthenticated(false);
  }, []);

  const initializeAuth = useCallback(async () => {
    let activeSession = await validateSession();
    if (!activeSession) {
      activeSession = await refreshSession();
    }

    if (!activeSession || !isAllowedRole(activeSession.role)) {
      setIsLoading(false);
      setIsFetching(false);
      return;
    }

    try {
      const profile = await fetchBffCurrentUser();
      if (profile && isAllowedRole(profile.role)) {
        setUser(profile as AuthContextType["user"]);
        setIsAuthenticated(true);
      }
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, []);

  const checkAuthSilently = useCallback(async () => {
    try {
      const session = await validateSession();
      if (!session) handleAuthError();
    } catch {
      // ignore
    }
  }, [handleAuthError]);

  const refreshAuthSilently = useCallback(async () => {
    try {
      const session = await refreshSession();
      if (!session || !isAllowedRole(session.role)) {
        handleAuthError();
        return;
      }
      const profile = await fetchBffCurrentUser();
      if (profile && isAllowedRole(profile.role)) {
        setUser(profile as AuthContextType["user"]);
        setIsAuthenticated(true);
      } else {
        handleAuthError();
      }
    } catch {
      handleAuthError();
    }
  }, [handleAuthError]);

  useEffect(() => {
    void initializeAuth();
  }, [initializeAuth]);

  useEffect(() => {
    return setupAxiosSessionRefresh();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      void checkAuthSilently();
    }, SESSION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, checkAuthSilently]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      void refreshAuthSilently();
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshAuthSilently]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshAuthSilently();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isAuthenticated, refreshAuthSilently]);

  const contextValue: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    isFetching,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
